/* eslint-disable max-lines */
/* eslint-disable complexity */
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypes } from '@strata/constants';
import { DateFormats, formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { RequestType, RequestTypeDisplayTextMap } from '@strata/react-chat';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FormSection } from '@strata/react-form-section';
import { type ColumnDef, Grid, SecondaryData } from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { MessagingBlock } from '@strata/react-messaging-block';
import { MultiIssueDetail } from '@strata/react-status-icons';
import {
    type OpportunityStatus,
    OpportunityStatusIndicator,
    type ReferralStatusIndicator,
    TreatmentStatusIndicator,
} from '@strata/react-status-indicators';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ReferralAndOnboardingStatusIndicator } from '../shared/ReferralAndOnboardingStatusIndicator';

import { ReferralDetailsOverview } from 'components/referrals/ReferralDetailsOverview';
import { SubmitRequestDialog } from 'components/requests/dialogs/SubmitRequestDialog';
import { SpecialMemberHandlingName } from 'components/shared/SpecialMemberHandlingName';
import { ActiveRequestDetails } from 'components/treatments/ActiveRequestDetails';
import { ArchivedRequestDetails } from 'components/treatments/ArchivedRequestDetails';
import { AuthDetails } from 'components/treatments/AuthDetails';
import { UpdateMemberPhoneNumberDialog } from 'components/treatments/dialogs/UpdateMemberPhoneNumberDialog';
import { IntakeCaseDetails } from 'components/treatments/IntakeCaseDetails';
import { TreatmentMemberDetails } from 'components/treatments/TreatmentMemberDetails';
import { Permissions } from 'services/constants';
import { PayerFeatures, usePayerFeatures } from 'services/FeatureFlags';
import { getMostCurrentReferralId, useIntakeCasesForTreatment } from 'services/intakeCaseHelpers';
import { type BackToDestination, useGoBack } from 'services/navigationHelpers';
import { AppRoutes } from 'services/routes';
import { useScopeAssessorMemberDetails } from 'services/useScopeAssessorMemberDetails';
import { useSpecialMemberHandling } from 'services/useSpecialMemberHandling';
import { useAppSelector } from 'store';
import {
    type OpportunitySummary,
    useGetOpportunityByTreatmentQuery,
} from 'store/api/opportunityCuratorApi';
import { useGetNeedsByDateQuery } from 'store/api/performanceApi';
import {
    type SupplierMap,
    useGetAllProductServicesQuery,
    useGetSupplierMapQuery,
} from 'store/api/referenceApi';
import { type TreatmentReferralSummary, useGetTreatmentDetailsQuery } from 'store/api/treatmentApi';

const initialSortProperties = [{ id: 'receivedTime', desc: true }];

const backToDestinationDefault = {
    route: AppRoutes.treatments,
    text: 'Member Search',
};

const currentLocation: BackToDestination = {
    text: 'Member Treatment Details',
};

type ReferralStatus =
    (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];

interface ReferralSummary
    extends Omit<TreatmentReferralSummary, 'referralStatus'>, Pick<OpportunitySummary, 'issues'> {
    referralStatus: OpportunityStatus | ReferralStatus;
}

const columns = ({
    supplierMap,
    showOpportunityDetailsInPayersApp,
}: {
    supplierMap?: SupplierMap;
    showOpportunityDetailsInPayersApp: boolean;
}): ColumnDef<ReferralSummary>[] => [
    {
        accessorKey: 'friendlyId',
        header: 'Referral ID',
        size: 80,
    },
    {
        accessorKey: 'authId',
        header: 'Authorization ID',
        size: 80,
    },
    {
        accessorKey: 'servicingSupplierNpi',
        header: 'Servicing Pharmacy',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={supplierMap?.[getValue() ?? '']?.name} />,
    },
    {
        accessorKey: 'routeName',
        header: 'Route Rule',
        size: 80,
    },
    {
        accessorKey: 'benefitType',
        header: 'Benefit',
        size: 80,
    },
    {
        accessorKey: 'receivedTime',
        header: 'Received Date',
        size: 80,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'referralStatus',
        header: 'Referral Status',
        size: 80,
        cell: ({ getValue, row }) => {
            const value = getValue();
            if (value in OpportunityStatusIndicator.Statuses && showOpportunityDetailsInPayersApp) {
                return <OpportunityStatusIndicator status={value as OpportunityStatus} />;
            }
            return (
                <ReferralAndOnboardingStatusIndicator
                    status={value as ReferralStatus}
                    referralId={row.original.referralId}
                />
            );
        },
    },
    ...(showOpportunityDetailsInPayersApp
        ? [
              {
                  accessorKey: 'issues',
                  header: 'Status Details',
                  size: 80,
                  cell: ({ getValue }) => {
                      const issues = getValue();
                      return issues ? (
                          <MultiIssueDetail issues={issues} />
                      ) : (
                          <SecondaryData data={undefined} />
                      );
                  },
              } as ColumnDef<ReferralSummary>,
          ]
        : []),
];

export const TreatmentDetailPage = () => {
    let { treatmentId } = useParams() as { treatmentId: string };
    treatmentId = decodeURIComponent(treatmentId);
    const navigate = useNavigate();

    const { showOpportunityDetailsInPayersApp, showUpdateMemberPhoneNumberDialog } = useFlags<{
        showOpportunityDetailsInPayersApp: boolean;
        showUpdateMemberPhoneNumberDialog: boolean;
    }>();
    const payerFeatures = usePayerFeatures();
    const { payerId, permissions } = useAppSelector((state) => state.auth);
    const canSubmitMemberEscalation =
        payerFeatures?.[PayerFeatures.REQUESTS] &&
        permissions.includes(Permissions.WRITE_PAYER_REQUEST_ESCALATION);
    const canSeeMemberEscalations =
        payerFeatures?.[PayerFeatures.REQUESTS] &&
        permissions.includes(Permissions.READ_PAYER_REQUEST_ESCALATION);

    const canUpdateMemberPhoneNumber = permissions.includes(Permissions.WRITE_PAYER_REQUEST_UPDATE);

    const {
        data: treatmentDetails,
        isError: getTreatmentDetailsError,
        isFetching: treatmentFetching,
    } = useGetTreatmentDetailsQuery({
        payerId: payerId!,
        treatmentId,
    });

    const { intakeCases, intakeCasesVisible, intakeCasesFetching } = useIntakeCasesForTreatment({
        treatmentDetails,
    });

    const { data: opportunityDetails } = useGetOpportunityByTreatmentQuery(
        showOpportunityDetailsInPayersApp
            ? {
                  treatmentId,
              }
            : skipToken,
    );
    const showOpportunityDetails = showOpportunityDetailsInPayersApp && !!opportunityDetails;
    const mostCurrentReferralId = getMostCurrentReferralId(treatmentDetails?.referralList);
    const treatmentOverviewReferralId = showOpportunityDetails
        ? opportunityDetails?.referralId
        : mostCurrentReferralId;

    const specialMemberHandling = useSpecialMemberHandling(treatmentDetails, {
        routeCode: treatmentDetails?.activeReferral?.routeCode,
        supplierNpi: treatmentDetails?.activeReferral?.servicingSupplierNpi,
    });

    const { memberDetails, willBeUnmanagedOn } = useScopeAssessorMemberDetails(treatmentDetails, {
        computeWillBeUnmanagedOn: true,
    });

    const showRequests = canSeeMemberEscalations;

    const { data: products } = useGetAllProductServicesQuery();
    const { productServiceMap } = products ?? {};
    const { data: supplierMap } = useGetSupplierMapQuery();

    const { backToDestination, goBack } = useGoBack(backToDestinationDefault);

    useEffect(() => {
        if (getTreatmentDetailsError) {
            goBack();
        }
    }, [goBack, getTreatmentDetailsError]);

    const activeReferralIsPharmacyBenefit =
        treatmentDetails?.activeReferral?.benefitTypeRaw === BenefitTypes.PHARMACY;

    const { data: needsByDateData } = useGetNeedsByDateQuery(
        treatmentDetails && activeReferralIsPharmacyBenefit
            ? { treatmentId: treatmentDetails.treatmentId }
            : skipToken,
    );

    const [isSubmitRequestDialogOpen, setIsSubmitRequestDialogOpen] = useState(false);
    const [isUpdateMemberPhoneNumberDialogOpen, setIsUpdateMemberPhoneNumberDialogOpen] =
        useState(false);

    const menuItems = [
        ...(canSubmitMemberEscalation
            ? [
                  {
                      text: `Submit ${RequestTypeDisplayTextMap[RequestType.ESCALATION]?.replace(
                          ' Inquiry',
                          '',
                      )} Request`,
                      onItemClick: () => setIsSubmitRequestDialogOpen(true),
                  },
              ]
            : []),
        ...(showUpdateMemberPhoneNumberDialog && canUpdateMemberPhoneNumber
            ? [
                  {
                      text: 'Update Member Phone Number',
                      onItemClick: () => setIsUpdateMemberPhoneNumberDialogOpen(true),
                  },
              ]
            : []),
    ];

    const referralList = treatmentDetails?.referralList?.map((referral) => {
        if (referral.referralId === opportunityDetails?.referralId) {
            return {
                ...referral,
                ...opportunityDetails,
            };
        }
        return referral as ReferralSummary;
    });

    return (
        <FormHeaderAndContent
            rowOneContentHeader={
                <>
                    <ActionButtonPageTitle
                        label={backToDestination.text}
                        title={currentLocation.text}
                        id="treatment-details-back-button"
                        onClick={() => goBack()}
                    />
                    {menuItems.length > 0 && (
                        <div style={{ marginLeft: 'auto' }}>
                            <ButtonWithDropdownMenu
                                label="Actions"
                                menuItems={menuItems}
                                intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                            />
                        </div>
                    )}
                    <SubmitRequestDialog
                        isOpen={isSubmitRequestDialogOpen}
                        onClose={() => setIsSubmitRequestDialogOpen(false)}
                        treatmentDetails={treatmentDetails}
                    />
                    <UpdateMemberPhoneNumberDialog
                        isOpen={isUpdateMemberPhoneNumberDialogOpen}
                        onClose={() => setIsUpdateMemberPhoneNumberDialogOpen(false)}
                        treatmentDetails={treatmentDetails}
                        memberDetails={memberDetails}
                    />
                </>
            }
            rowThreeContentHeader={
                !treatmentFetching && (
                    <>
                        <FormTitleLabelValuePair
                            label={
                                <SpecialMemberHandlingName
                                    name={formatNameLastFirst(
                                        treatmentDetails?.lastName,
                                        treatmentDetails?.firstName,
                                    )}
                                    tooltip={specialMemberHandling?.tooltip}
                                />
                            }
                            value={memberDetails?.memberId}
                        />
                        <HeaderLabelValuePair
                            label="Product"
                            value={productServiceMap?.[treatmentDetails?.productServiceOid ?? '']}
                        />
                        <HeaderLabelValuePair
                            label="Status"
                            value={
                                treatmentDetails?.treatmentStatus && (
                                    <TreatmentStatusIndicator
                                        status={treatmentDetails.treatmentStatus}
                                    />
                                )
                            }
                        />
                        <HeaderLabelValuePair
                            label="Next Fill Needs By Date"
                            value={formatDate(needsByDateData?.needsByDate)}
                        />
                    </>
                )
            }
            mainContent={
                <>
                    <CollapsibleSection
                        title="Treatment Overview"
                        className="sds_treatment__active-section-header"
                        content={
                            <div className="sds_details-page-overview">
                                <div
                                    className="sds_details-page-overview__container"
                                    style={{ flex: '2 0' }}
                                >
                                    <FormSection
                                        sectionTitle="Referrals"
                                        className="sds_treatment__grid"
                                        content={
                                            <>
                                                {showOpportunityDetails ? (
                                                    <div className="padding__bottom--medium">
                                                        <InlineMessage
                                                            label="Referral data will be available after all required information has been collected."
                                                            intent={InlineMessage.Intent.INFO}
                                                        />
                                                    </div>
                                                ) : null}
                                                <Grid
                                                    enableSorting
                                                    data={referralList}
                                                    isLoading={treatmentFetching}
                                                    sortProperties={initialSortProperties}
                                                    noResults={
                                                        <MessagingBlock
                                                            size={MessagingBlock.Sizes.SMALL}
                                                            icon={IconNames.CLIPBOARD}
                                                            primaryText="No referrals"
                                                        />
                                                    }
                                                    onRowClick={(row) => {
                                                        navigate(
                                                            AppRoutes.referralDetails(
                                                                row.referralId,
                                                            ),
                                                            {
                                                                state: {
                                                                    backToDestination:
                                                                        currentLocation,
                                                                },
                                                            },
                                                        );
                                                    }}
                                                    rowCssClass={(row) =>
                                                        classNames('sds_treatment__grid__row', {
                                                            'sds_treatment__grid__row--active':
                                                                row.referralId ===
                                                                mostCurrentReferralId,
                                                        })
                                                    }
                                                    columns={columns({
                                                        supplierMap,
                                                        showOpportunityDetailsInPayersApp,
                                                    })}
                                                />
                                            </>
                                        }
                                    />
                                    <FormSection
                                        sectionTitle="Authorizations"
                                        content={
                                            <AuthDetails
                                                auths={treatmentDetails?.authList}
                                                isContingentTherapy={
                                                    treatmentDetails?.isContingentTherapy
                                                }
                                                isLoading={treatmentFetching}
                                            />
                                        }
                                    />
                                    {intakeCasesVisible && (
                                        <FormSection
                                            sectionTitle="Prior Auth Requests"
                                            content={
                                                <IntakeCaseDetails
                                                    cases={intakeCases}
                                                    isLoading={intakeCasesFetching}
                                                />
                                            }
                                        />
                                    )}
                                    {showRequests && (
                                        <>
                                            <ActiveRequestDetails
                                                treatmentId={treatmentId}
                                                payerId={payerId}
                                                memberId={memberDetails?.memberId}
                                                showRequests={showRequests}
                                                currentLocation={currentLocation}
                                            />
                                            <ArchivedRequestDetails
                                                treatmentId={treatmentId}
                                                payerId={payerId}
                                                memberId={memberDetails?.memberId}
                                                showRequests={showRequests}
                                                currentLocation={currentLocation}
                                            />
                                        </>
                                    )}
                                </div>
                                <div
                                    className="sds_details-page-overview__container"
                                    style={{ flex: '1 0' }}
                                >
                                    <TreatmentMemberDetails
                                        memberDetails={memberDetails}
                                        willBeUnmanagedOn={willBeUnmanagedOn}
                                    />
                                </div>
                            </div>
                        }
                    />
                    {treatmentOverviewReferralId && (
                        <CollapsibleSection
                            title="Active Referral Overview"
                            className={classNames(
                                'padding__top--x-large',
                                'sds_treatment__active-section-header',
                            )}
                            content={
                                <ReferralDetailsOverview
                                    referralId={treatmentOverviewReferralId}
                                    currentLocation={currentLocation}
                                    treatmentDetailsForIntakeMilestones={treatmentDetails}
                                />
                            }
                        />
                    )}
                </>
            }
        />
    );
};
